import {getWeekdayLabel} from '@wix/events-moment-commons'
import {handleKeyPressEnterOrSpace} from '@wix/panda-js-utils'
import {widgetDataHooks as DH} from '@wix/wix-events-data-hooks'
import {SkeletonLine} from '@wix/wix-ui-tpa/cssVars'
import {useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React from 'react'
import {useWidgetActions, useWidgetState} from '../../../../hooks/state-provider'
import {
  getDayMonthText,
  getDayNumber,
  getEventsForDay,
  getToday,
  getWeekdayName,
  isCalendarPageLoading,
  isDayInCurrentMonth,
  isDayWeekend,
} from '../../../../selectors/calendar-layout'
import s from './cell.scss'
import {DayEvents} from './day-events'
import {CalendarCellProps} from './interfaces'

export const CalendarCell = ({timestamp, columnIndex}: CalendarCellProps) => {
  const {openMonthlyCalendarEvent, openMonthlyCalendarPopup} = useWidgetActions()
  const {t} = useTranslation()

  const todayDate = useWidgetState(getToday)
  const dayNumber = useWidgetState(state => getDayNumber(state, timestamp))
  const events = useWidgetState(state => getEventsForDay(state, timestamp))
  const inCurrentMonth = useWidgetState(state => isDayInCurrentMonth(state, timestamp))
  const weekend = useWidgetState(state => isDayWeekend(state, timestamp))
  const dateText = useWidgetState(state => getDayMonthText(state, timestamp))
  const weekDayName = useWidgetState(state => getWeekdayName(state, columnIndex, getWeekdayLabel))
  const loading = useWidgetState(isCalendarPageLoading)

  const today = timestamp === todayDate

  const openPopup = () => {
    if (events.length === 1) {
      openMonthlyCalendarEvent({eventId: events[0].id, selectedDate: timestamp})
    } else {
      openMonthlyCalendarPopup(timestamp)
    }
  }

  return (
    <div
      className={s.cell}
      data-hook={DH.calendarCell(timestamp)}
      aria-label={t('a11y.calendarCellLabel', {selectedDate: dateText, weekday: weekDayName, count: events.length})}
      tabIndex={events.length && !loading ? 0 : -1}
      role="button"
      onKeyDown={events.length && !loading ? handleKeyPressEnterOrSpace(openPopup) : undefined}
      onClick={events.length && !loading ? openPopup : undefined}
    >
      <div
        className={classNames(s.content, {
          [s.weekend]: weekend && !today,
          [s.today]: today && !weekend && !loading,
          [s.weekendToday]: today && weekend && !loading,
          [s.hasEvents]: events.length > 0 && !loading,
        })}
      >
        {loading ? (
          <SkeletonLine className={s.skeletonLine} />
        ) : (
          <>
            <div className={classNames(s.dayNumber, {[s.otherMonth]: !inCurrentMonth})}>{dayNumber}</div>
            {!!events.length && <DayEvents events={events} currentDate={timestamp} />}
          </>
        )}
      </div>
    </div>
  )
}
